import { FetchInputType } from "../types/fetch-params";

const NEXT_PUBLIC_API_URL = process.env.NEXT_PUBLIC_API_URL ?? "";

/**
 * Convert an absolute upstream URL to a same-origin proxy path so the request
 * hits the `vercel.json` `routes` rule, which injects the deployment
 * protection bypass header and forwards to BACKEND_API_ORIGIN.
 *
 *   in:  "https://qa-api.innerbalance.com/api/v1/auth/me?x=1"
 *   out: "/api/v1/auth/me?x=1"
 *
 *   in:  "https://third-party.example.com/track"
 *   out: null  (never proxy non-upstream URLs)
 *
 *   in:  "https://qa-api.innerbalance.com/wp-admin/foo"  ← same origin, OFF the API path
 *   out: null  (proxy would 404, surfacing a false `api-proxy-misconfigured`)
 *
 * Returns `null` for:
 *   • URLs whose origin does not match NEXT_PUBLIC_API_URL (third-party)
 *   • URLs whose pathname is NOT under NEXT_PUBLIC_API_URL's path prefix —
 *     these don't match `vercel.json` `routes` and would 404 on our own
 *     origin, polluting the `api-proxy-misconfigured` Datadog stream
 *   • `Request` objects (not currently used by callers, defensive)
 *   • malformed URLs (caught & returned as null)
 *   • when NEXT_PUBLIC_API_URL is unset (e.g. test environment without env)
 */
export function toProxyUrl(input: FetchInputType): string | null {
    if (!NEXT_PUBLIC_API_URL) return null;

    const href =
        typeof input === "string"
            ? input
            : input instanceof URL
                ? input.href
                : null;
    if (!href) return null;

    try {
        const url = new URL(href);
        const apiBase = new URL(NEXT_PUBLIC_API_URL);
        if (url.origin !== apiBase.origin) return null;

        // Path prefix check. Normalize trailing slash so `"/api/"` and `"/api"`
        // behave the same, then enforce a boundary (`startsWith` alone would
        // false-positive on `/apipa/...`). When apiBase has no path (`"/"`),
        // `basePath` collapses to `""` and the check is skipped — degrading
        // gracefully to the legacy origin-only behaviour.
        const basePath = apiBase.pathname.replace(/\/+$/, "");
        if (basePath
            && url.pathname !== basePath
            && !url.pathname.startsWith(basePath + "/")
        ) {
            return null;
        }

        return url.pathname + url.search;
    } catch {
        return null;
    }
}

/**
 * Best-effort stringification of `fetch()` first argument for logging /
 * Datadog payloads. `Request` instances expose `.url` but we keep them
 * `undefined` for now — callers (`useFetchBase`) don't use `Request`.
 */
export function stringifyInput(input: FetchInputType): string | undefined {
    if (typeof input === "string") return input;
    if (input instanceof URL) return input.href;
    return undefined;
}
